import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Drawer, PriorityBadge, Row, Tag, Typography } from 'components';
import { usePurchaseOrderItemSalesPriceQuery } from 'api';

const { Text } = Typography;

interface ISalesPriceDrawer {
  onExpand: () => void;
  drawerState: boolean;
  data?: string[];
}

export const SalesPriceDrawer: FC<ISalesPriceDrawer> = React.memo((props) => {
  const { onExpand, drawerState, data } = props;

  const { data: purchaseOrderItemSalesPrice = {} } = usePurchaseOrderItemSalesPriceQuery(
    { ids: data },
    { skip: !data }
  );
  const { purchaseOrderItemCommercialOffersResponses = [], itemSku, itemId } = purchaseOrderItemSalesPrice || {};

  return (
    <Drawer
      closeIcon={null}
      placement="left"
      width={471}
      open={drawerState}
      onClose={() => onExpand()}
      styles={{
        header: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
      }}
      title={<Text className={'font-weight-800 font-size-20'}>Commercial Offers</Text>}
    >
      <div style={{ position: 'absolute', right: 16, top: 16 }}>
        <Button icon={<CloseOutlined />} onClick={() => onExpand()} type="text" />
      </div>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Row align={'middle'} className={'gap-4'}>
            <Text className={'font-size-16 font-weight-500'}>Item SKU - </Text>
            <Link to={`${ADMIN_ROUTES.items.view}/${itemId}/suppliers`} target={'_blank'}>
              <Text underline className={'font-size-16 font-weight-500'} ellipsis={{ tooltip: true }}>
                {itemSku}
              </Text>
            </Link>
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={[0, 24]}>
            {purchaseOrderItemCommercialOffersResponses?.map((_item: Record<string, any>) => (
              <Col span={24} key={_item.commercialOfferId}>
                <Row gutter={[0, 8]}>
                  <Col span={24}>
                    <Link
                      to={`${ADMIN_ROUTES.deals.view}/${_item.dealId}/${ADMIN_ROUTES.deals.commercialOfferUpdate}/${_item.commercialOfferId}`}
                      target={'_blank'}
                    >
                      <Text underline className={'font-size-16 font-weight-600'} ellipsis={{ tooltip: true }}>
                        Commercial Offer: {_item.commercialOfferUuid}
                      </Text>
                    </Link>
                  </Col>
                  <Col span={24}>
                    <Row align={'middle'} justify={'space-between'}>
                      <Col>
                        <Link to={`${ADMIN_ROUTES.client}/${_item.client?.id}/general`} target={'_blank'}>
                          <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                            {_item.client?.name}
                          </Text>
                        </Link>
                      </Col>
                      <Col>
                        <PriorityBadge tooltip={_item.client?.priorityRationale} type={_item.client?.priorityLevel} />
                      </Col>
                    </Row>
                  </Col>
                  {!!_item.client?.tags?.length && (
                    <Col span={24}>
                      <Row className={'gap-4'}>
                        {_item.client.tags.map((_tag: string, index: number) => (
                          <Tag title={_tag} key={index + _tag} className={'custom-tag'}>
                            {_tag}
                          </Tag>
                        ))}
                      </Row>
                    </Col>
                  )}
                  {_item.client?.additionalNotes && (
                    <Col span={24}>
                      <Text type={'secondary'}>
                        <Text className={'font-weight-500'} type={'secondary'}>
                          Additional Notes:{' '}
                        </Text>
                        {_item.client?.additionalNotes}
                      </Text>
                    </Col>
                  )}
                  <Divider className={'divider'} />
                  <Col span={24}>
                    <Row>
                      <Col span={24}>
                        <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                          <Col flex="none">
                            <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                              Deal ID
                            </Text>
                          </Col>
                          <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Link to={`${ADMIN_ROUTES.deals.view}/${_item.dealId}/purchase-request`} target={'_blank'}>
                              <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.dealUuid || 'Not Set'}
                              </Text>
                            </Link>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                          <Col flex="none">
                            <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                              Order Number
                            </Text>
                          </Col>
                          <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Link to={`${ADMIN_ROUTES.orders.view}/${_item.clientOrderId}/general`} target={'_blank'}>
                              <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.clientOrderUuid || 'Not Set'}
                              </Text>
                            </Link>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                          <Col flex="none">
                            <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                              Item qty
                            </Text>
                          </Col>
                          <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                              {_item.quantity ? `${_item.quantity}` : 'Not Set'}
                            </Text>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                          <Col flex="none">
                            <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                              Sales Price
                            </Text>
                          </Col>
                          <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                              {_item.salesPrice ? `€${_item.salesPrice}` : 'Not Set'}
                            </Text>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                          <Col flex="none">
                            <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                              Purchase Price
                            </Text>
                          </Col>
                          <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                              {_item.purchasePrice ? `€${_item.purchasePrice}` : 'Not Set'}
                            </Text>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                          <Col flex="none">
                            <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                              Margin
                            </Text>
                          </Col>
                          <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                              {_item.margin ? `${_item.margin}%` : 'Not Set'}
                            </Text>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Drawer>
  );
});
